// import TablePagination from "@/components/table/pagination";
import PermissionsLists from "@/config/PermissionsLists";
import { EndPoints, httpService, Operation } from "@/Core/Client";
import { useState } from "react";

// import TablePagination from "@/components/table/pagination";
import { Config } from "@/components/crud/crud-config";
import CrudFormElements from "@/components/crud/crud-form-elements";
import CrudPage from "@/components/crud/page";
import PencilIcon from "@/components/icons/pencil";
import TableActions from "@/components/table-actions";
import useQueryKeys from "@/config/query-keys";
import { ERolesGuard } from "@/Core/enums";
import { DATE_FORMAT_OPTIONS } from "@/Core/Helpers/DayjsHelpers";
import { RoleFormDto, roleFormSchema, RoleResponseDto } from "@/Dtos/role-dto";
import AvatarLoaderCard from "@/ui/avatar-card-loader";
import DateCell from "@/ui/date-cell";
import { useQueryClient } from "@tanstack/react-query";
import { createColumnHelper } from "@tanstack/react-table";
import toast from "react-hot-toast";
import { FcPrivacy } from "react-icons/fc";
import { Badge, Checkbox, Flex, Text } from "rizzui";
import { useModal } from "../shared/modal-views/use-modal";
import AssignPermissionsForm from "./assign-form";

type responseType = RoleResponseDto;
type formType = RoleFormDto;

const config: Config<responseType, formType> = {
    endPoint: EndPoints.role.crud,
    queryKey: useQueryKeys.roles,
    columns: [],
    validationSchema: roleFormSchema,
    fields: [
        {
            name: "name",
            label: "Name",
            type: "text",
            hasFilter: true,
            // className: "w-auto",
            filter: {
                operation: Operation.like,
                // showOperations: true,
            },
        },
        {
            name: "entityId",
            label: "Entity",
            type: "referTo",
            hasFilter: true,
            referEndPoint: EndPoints.entity.referTo,
            filter: {
                operation: Operation.eq,
            },
        },
        {
            name: "createdAt",
            label: "Created At",
            type: "date",
            hasFilter: true,
            filterOnly: true,
            dateFormat: DATE_FORMAT_OPTIONS.L,
            // className: "w-auto",
            filter: {
                operation: Operation.gte,
                // showOperations: true,
            },
        },
    ],
    titles: {
        pageTitle: "Roles & Permissions",
        pageCreateTitle: "Create Role",
    },
    filterProps: {
        useDrawer: true,
        // ignoreSorting: true,
        sortingClassName: "w-auto",
        // sortingMinWidth: "10px",
        directionClassName: "w-auto",
        // directionMinWidth: "10px",
    },

    permissionsCreate: PermissionsLists.Role.Create,
    formOptions: {
        customSize: "750px",
    },
};

const columnHelper = createColumnHelper<responseType>();

const Columns = [
    columnHelper.display({
        id: "select",
        size: 50,
        header: ({ table }) => (
            <Checkbox
                className="ps-3.5"
                aria-label="Select all Rows"
                checked={table.getIsAllPageRowsSelected()}
                onChange={() => table.toggleAllPageRowsSelected()}
            />
        ),
        cell: ({ row }) => (
            <Checkbox
                className="ps-3.5"
                aria-label="Select Row"
                checked={row.getIsSelected()}
                onChange={row.getToggleSelectedHandler()}
            />
        ),
    }),

    columnHelper.accessor("name", {
        id: "name",
        size: 200,
        header: "Name",
        enableSorting: true,
        cell: ({ row: { original } }) => (
            <Text className="text-[13px] text-gray-500">{original.name}</Text>
        ),
    }),

    columnHelper.accessor("entity.name", {
        id: "entity",
        size: 140,
        header: "Entity",
        cell: ({ row: { original } }) => (
            <AvatarLoaderCard
                name={original.entity?.name ?? "--"}
                description={original.entity?.phone ?? "--"}
            />
        ),
    }),

    columnHelper.display({
        id: "permissions",
        size: 250,
        header: "Permissions",
        cell: ({ row: { original } }) => (
            <Flex align="center" gap="2">
                <Badge
                    rounded="lg"
                    key={"permissions"}
                    variant="outline"
                    className="border-muted font-normal text-gray-500"
                >
                    {original?.permissions?.length ?? 0}
                </Badge>
            </Flex>
        ),
    }),
    columnHelper.accessor("createdAt", {
        id: "createdAt",
        size: 160,
        header: "Created",
        cell: ({ row }) => (
            <DateCell
                date={new Date(Date.parse(row.original?.createdAt ?? ""))}
            />
        ),
    }),
    columnHelper.display({
        id: "actions",
        size: 80,
        cell: ({ row }) => {
            const { openModal } = useModal();
            const [isLoading, setLoading] = useState(false);
            const queryClient = useQueryClient();

            const reload = async () =>
                await queryClient.refetchQueries({
                    queryKey: config.queryKey,
                });

            return (
                <TableActions
                    row={row.original}
                    permissionDelete={PermissionsLists.Role.Delete}
                    onDelete={async () => {
                        setLoading(true);
                        await httpService(config.endPoint).delete(
                            row.original.id!,
                        );
                        setLoading(false);
                        await reload();
                    }}
                    actions={[
                        {
                            title: "Assign Permission",
                            isLoading: isLoading,
                            icon: <FcPrivacy className="size-4" />,
                            onClick: () =>
                                row.original?.guard === ERolesGuard.System_Role
                                    ? toast("this role is not Modifiable", {
                                        icon: "🔒",
                                    })
                                    : openModal({
                                        customSize: "1550px",
                                        view: (
                                            <AssignPermissionsForm
                                                edit={row.original}
                                                callback={() => reload()}
                                            />
                                        ),
                                    }),
                        },
                        {
                            title: "View",
                            color: "gray",
                            isLoading: isLoading,
                            icon: <PencilIcon className="size-4" />,
                            onClick: () =>
                                openModal({
                                    view: (
                                        <CrudFormElements<
                                            responseType,
                                            formType
                                        >
                                            editData={row.original}
                                            config={config}
                                        />
                                    ),
                                    customSize: config.formOptions?.customSize,
                                }),
                        },
                    ]}
                />
            );
        },
    }),
];

config.columns = Columns;

export default function MainPage() {
    return CrudPage<responseType, formType>({ config: config });
}
